import type { HostTutorBookingResponse, TutorBookingsPageResponse, TutorSessionType } from '../models/Tutor';
import { tutorApiClient } from '../tutorApiClient';
import { stripCalendarInstant } from './stripCalendarInstant';
import { formatLocalDateKey, parseWallClockInstant } from './wallClockInstant';
import { bookingWallClockToUtc, parseBookingCalendarInstant, serializeBookingInstantForApi } from './bookingInstant';
import type { CalendarEvent } from '../models/CalendarEvent';
import { CalendarEventSource, CalendarEventStatus } from '../models/CalendarEvent';
import { zonedTimeToUtc } from 'date-fns-tz';
import { isAtStudentSession, isAtTeacherSession, isOnlineSession, normalizeTutorSessionType } from './tutorSessionType';
import { tutorBookingStudentNames } from './tutorBookingStudentName';

export type TutorCalendarLessonKind = 'confirmed' | 'pending' | 'external' | 'manual';

export interface TutorCalendarLessonEvent {
  id: string;
  bookingId?: string;
  calendarEventId?: string;
  title: string;
  /**
   * Absolute instant as UTC LocalDateTime (no `Z`), for calendar placement.
   * Booking rows are derived from API wall clocks via {@link mapBookingToLessonEvent}
   * using the booking display timezone.
   */
  startTime: string;
  endTime: string;
  /**
   * Booking display IANA timezone from the API (`tutor → space → UTC`).
   * Present on booking-backed events; used when formatting schedule labels.
   */
  timezone?: string | null;
  kind: TutorCalendarLessonKind;
  status?: string;
  studentUserId?: string | null;
  studentName?: string | null;
  /** Student phone (E.164) from booking list. */
  studentPhoneNumber?: string | null;
  /** Student profile band for “Plays in …”. */
  bandName?: string | null;
  studentProfileImageUrl?: string | null;
  lessonName?: string | null;
  sessionType?: TutorSessionType | null;
  tutorId?: string;
  tutorName?: string | null;
  tutorProfileImageUrl?: string | null;
  spaceId?: string | null;
  spaceName?: string | null;
  roomId?: string | null;
  roomName?: string | null;
  roomCalendarEventId?: string | null;
}

const PENDING_STATUSES = new Set([
  'AWAITING_CONFIRMATION',
  'PENDING_CONFIRMATION',
  'PENDING',
  'SUBMITTED',
  'NEW',
]);

const INACTIVE_STATUSES = new Set(['CANCELLED', 'DECLINED', 'REJECTED']);

export function isPendingTutorBookingStatus(status: string | undefined | null): boolean {
  if (!status) return false;
  return PENDING_STATUSES.has(status.toUpperCase());
}

function isActiveBooking(booking: HostTutorBookingResponse): boolean {
  const status = booking.status?.toUpperCase() ?? '';
  return !INACTIVE_STATUSES.has(status);
}

function bookingOverlapsRange(booking: HostTutorBookingResponse, rangeStart: Date, rangeEnd: Date): boolean {
  const start = parseWallClockInstant(booking.startTime);
  const end = parseWallClockInstant(booking.endTime);
  return start < rangeEnd && end > rangeStart;
}

function normalizeBookingDisplayInstant(iso: string, timezone: string): string {
  return serializeBookingInstantForApi(
    bookingWallClockToUtc(parseWallClockInstant(stripCalendarInstant(iso)), timezone),
  );
}

function buildLessonTitle(
  booking: HostTutorBookingResponse,
  fallback: string,
  walkInLabel = 'Walk-in',
): string {
  const customTitle = booking.title?.trim();
  const student = tutorBookingStudentNames(booking).primaryName;
  // Walk-ins have no student; prefer the host-provided calendar title when present.
  if (!student && customTitle) return customTitle;
  const lesson = booking.lessonOptionName?.trim();
  if (student && lesson) return `${student} · ${lesson}`;
  if (student) return student;
  if (lesson) return `${walkInLabel} · ${lesson}`;
  return walkInLabel || fallback;
}

export function mapBookingToLessonEvent(
  booking: HostTutorBookingResponse,
  fallbackTitle: string,
  /**
   * Calendar/space fallback when the booking payload omits `timezone`.
   * Prefer `booking.timezone` — API wall clocks are already in that zone.
   */
  fallbackTimezone: string,
  walkInLabel = 'Walk-in',
): TutorCalendarLessonEvent {
  const pending = isPendingTutorBookingStatus(booking.status);
  // Handout: start/end are wall clocks in response `timezone` (tutor → space → UTC).
  // Convert to UTC with that zone — never assume the calendar grid timezone.
  const displayTimezone = booking.timezone?.trim() || fallbackTimezone.trim() || 'UTC';
  return {
    id: `booking-${booking.bookingId}`,
    bookingId: booking.bookingId,
    title: buildLessonTitle(booking, fallbackTitle, walkInLabel),
    startTime: normalizeBookingDisplayInstant(booking.startTime, displayTimezone),
    endTime: normalizeBookingDisplayInstant(booking.endTime, displayTimezone),
    timezone: displayTimezone,
    kind: pending ? 'pending' : 'confirmed',
    status: booking.status,
    studentUserId: booking.userId,
    studentName: tutorBookingStudentNames(booking).primaryName,
    studentPhoneNumber: booking.userPhoneNumber,
    bandName: booking.bandName,
    studentProfileImageUrl: booking.userProfileImageUrl,
    lessonName: booking.lessonOptionName,
    sessionType: normalizeTutorSessionType(booking.lessonSessionType),
    tutorId: booking.tutorId,
    tutorName: booking.tutorName,
    tutorProfileImageUrl: booking.tutorProfileImageUrl,
    spaceId: booking.spaceId,
    spaceName: booking.spaceName,
    roomId: booking.roomId ?? null,
    roomName: booking.roomName ?? null,
    roomCalendarEventId: booking.roomCalendarEventId ?? null,
  };
}

/** Room calendar event IDs that already represent an assigned tutor lesson (dedupe on unified grid). */
export function collectTutorLessonRoomCalendarEventIds(
  events: Pick<TutorCalendarLessonEvent, 'roomCalendarEventId'>[],
): Set<string> {
  const ids = new Set<string>();
  for (const event of events) {
    const id = event.roomCalendarEventId?.trim();
    if (id) ids.add(id);
  }
  return ids;
}

/** Resolve room / online label for host calendar titles and detail dialogs. */
export function resolveTutorEventLocationLabel(
  event: Pick<TutorCalendarLessonEvent, 'roomId' | 'roomName' | 'sessionType' | 'kind'>,
  options?: {
    rooms?: { id: string; name: string }[];
    onlineLabel?: string;
    atStudentLabel?: string;
    unassignedRoomLabel?: string;
  },
): string | null {
  if (event.kind === 'manual' || event.kind === 'external') {
    return null;
  }

  const fromEvent = event.roomName?.trim();
  if (fromEvent) return fromEvent;

  const roomId = event.roomId?.trim();
  if (roomId && options?.rooms?.length) {
    const match = options.rooms.find((room) => room.id === roomId);
    if (match?.name?.trim()) return match.name.trim();
  }

  if (isOnlineSession(event.sessionType)) {
    return options?.onlineLabel || 'Online';
  }

  if (isAtStudentSession(event.sessionType)) {
    return options?.atStudentLabel || "At student's place";
  }

  if (
    isAtTeacherSession(event.sessionType) ||
    event.kind === 'confirmed' ||
    event.kind === 'pending'
  ) {
    return options?.unassignedRoomLabel || 'No room';
  }

  return null;
}

export function enrichTutorEventRoomFromList(
  event: TutorCalendarLessonEvent,
  rooms: { id: string; name: string }[],
): TutorCalendarLessonEvent {
  if (event.roomName?.trim() || !event.roomId?.trim() || rooms.length === 0) {
    return event;
  }
  const match = rooms.find((room) => room.id === event.roomId);
  if (!match?.name?.trim()) return event;
  return { ...event, roomName: match.name.trim() };
}

export function formatHostUnifiedTutorEventTitle(
  event: TutorCalendarLessonEvent,
  options?: {
    unnamedTutor?: string;
    onlineLabel?: string;
    atStudentLabel?: string;
    unassignedRoomLabel?: string;
    rooms?: { id: string; name: string }[];
  },
): string {
  const tutorName = event.tutorName?.trim() || options?.unnamedTutor || 'Tutor';
  const base = event.title?.trim() || tutorName;
  const withTutor = event.tutorName?.trim() ? `${tutorName} · ${base}` : base;

  if (event.kind === 'manual' || event.kind === 'external') {
    return event.tutorName?.trim() ? `${tutorName} · ${base}` : base;
  }

  const roomName =
    event.roomName?.trim() ||
    (event.roomId?.trim()
      ? options?.rooms?.find((room) => room.id === event.roomId)?.name?.trim()
      : undefined);

  if (roomName) {
    return `${withTutor} @ ${roomName}`;
  }

  if (isOnlineSession(event.sessionType)) {
    return `${withTutor} · ${options?.onlineLabel || 'Online'}`;
  }

  if (isAtStudentSession(event.sessionType)) {
    return `${withTutor} · ${options?.atStudentLabel || "At student's place"}`;
  }

  if (isAtTeacherSession(event.sessionType)) {
    return `${withTutor} · ${options?.unassignedRoomLabel || 'No room'}`;
  }

  return withTutor;
}

export function tutorLessonClassName(kind: TutorCalendarLessonKind): string {
  switch (kind) {
    case 'pending':
      return 'tutor-lesson-pending';
    case 'manual':
      return 'tutor-lesson-manual';
    case 'external':
      return 'tutor-lesson-external';
    case 'confirmed':
    default:
      return 'tutor-lesson-confirmed';
  }
}

type FetchBookingsPage = (page: number, size: number) => Promise<TutorBookingsPageResponse>;

export async function fetchTutorBookingsForRange(
  fetchPage: FetchBookingsPage,
  rangeStart: Date,
  rangeEnd: Date,
  tutorId?: string,
): Promise<HostTutorBookingResponse[]> {
  const pageSize = 50;
  let page = 0;
  let totalPages = 1;
  const collected: HostTutorBookingResponse[] = [];

  while (page < totalPages) {
    const response = await fetchPage(page, pageSize);
    const content = response.content ?? [];
    totalPages = response.page?.totalPages ?? response.totalPages ?? (content.length < pageSize ? page + 1 : page + 2);

    for (const booking of content) {
      if (tutorId && booking.tutorId !== tutorId) continue;
      if (!isActiveBooking(booking)) continue;
      if (bookingOverlapsRange(booking, rangeStart, rangeEnd)) {
        collected.push(booking);
      }
    }

    const lastStart = content.length
      ? new Date(stripCalendarInstant(content[content.length - 1].startTime))
      : null;
    if (lastStart && lastStart < rangeStart && page + 1 >= totalPages) {
      break;
    }

    page += 1;
    if (content.length === 0) break;
  }

  return collected;
}

function intervalsOverlap(aStart: Date, aEnd: Date, bStart: Date, bEnd: Date): boolean {
  return aStart < bEnd && aEnd > bStart;
}

/**
 * Daily-availability HOST_BOOKING rows may be UTC instants or tutor-local wall clocks.
 * Booking events on the calendar are normalized to UTC. Try both interpretations.
 */
function calendarEventOverlapsBooking(
  calendarEvent: Pick<TutorCalendarLessonEvent, 'startTime' | 'endTime'>,
  booking: Pick<TutorCalendarLessonEvent, 'startTime' | 'endTime'>,
  timezone: string,
): boolean {
  const bookingStart = parseBookingCalendarInstant(booking.startTime);
  const bookingEnd = parseBookingCalendarInstant(booking.endTime);

  const asUtcStart = parseBookingCalendarInstant(calendarEvent.startTime);
  const asUtcEnd = parseBookingCalendarInstant(calendarEvent.endTime);
  if (intervalsOverlap(asUtcStart, asUtcEnd, bookingStart, bookingEnd)) {
    return true;
  }

  const asWallStart = zonedTimeToUtc(
    parseWallClockInstant(stripCalendarInstant(calendarEvent.startTime)),
    timezone,
  );
  const asWallEnd = zonedTimeToUtc(
    parseWallClockInstant(stripCalendarInstant(calendarEvent.endTime)),
    timezone,
  );
  return intervalsOverlap(asWallStart, asWallEnd, bookingStart, bookingEnd);
}

export async function loadTutorCalendarEventsForRange(
  tutorId: string,
  rangeStart: Date,
  rangeEnd: Date,
  timezone: string,
  fetchBookings: FetchBookingsPage,
  externalTitle: string,
  bookingTitleFallback: string,
  walkInLabel = 'Walk-in',
): Promise<TutorCalendarLessonEvent[]> {
  const [bookings, nonBookingCalendarEvents] = await Promise.all([
    fetchTutorBookingsForRange(fetchBookings, rangeStart, rangeEnd, tutorId),
    loadNonBookingCalendarEventsForRange(tutorId, rangeStart, rangeEnd, externalTitle),
  ]);

  const bookingEvents = bookings.map((booking) =>
    mapBookingToLessonEvent(booking, bookingTitleFallback, timezone, walkInLabel),
  );

  // HOST_BOOKING rows from daily availability duplicate walk-in/confirmed bookings
  // (and lack room fields). Drop any non-booking event that overlaps a booking.
  const dedupedCalendarEvents = nonBookingCalendarEvents
    .filter((calendarEvent) => {
      return !bookingEvents.some((booking) =>
        calendarEventOverlapsBooking(calendarEvent, booking, timezone),
      );
    })
    .map((calendarEvent) => ({
      ...calendarEvent,
      tutorId: calendarEvent.tutorId || tutorId,
    }));

  return [...bookingEvents, ...dedupedCalendarEvents].sort(
    (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
  );
}

/**
 * Load tutor calendar events for many tutors sharing one space (unified host calendar).
 * Bookings are fetched once; non-booking events are loaded per tutor.
 */
export async function loadSpaceTutorCalendarEventsForRange(
  tutors: { tutorId: string; name?: string | null; timezone?: string | null }[],
  rangeStart: Date,
  rangeEnd: Date,
  spaceTimezone: string,
  fetchBookings: FetchBookingsPage,
  externalTitle: string,
  bookingTitleFallback: string,
  walkInLabel = 'Walk-in',
): Promise<TutorCalendarLessonEvent[]> {
  if (tutors.length === 0) return [];

  const tutorIds = new Set(tutors.map((row) => row.tutorId));
  const tutorNameById = new Map(
    tutors.map((row) => [row.tutorId, row.name?.trim() || null] as const),
  );
  const tutorTimezoneById = new Map(
    tutors.map((row) => [row.tutorId, row.timezone?.trim() || spaceTimezone] as const),
  );

  const [bookingsSettled, nonBookingByTutor] = await Promise.all([
    fetchTutorBookingsForRange(fetchBookings, rangeStart, rangeEnd).then(
      (value): PromiseSettledResult<HostTutorBookingResponse[]> => ({
        status: 'fulfilled',
        value,
      }),
      (reason): PromiseSettledResult<HostTutorBookingResponse[]> => ({
        status: 'rejected',
        reason,
      }),
    ),
    Promise.all(
      tutors.map(async (tutor) => {
        try {
          return await loadNonBookingCalendarEventsForRange(
            tutor.tutorId,
            rangeStart,
            rangeEnd,
            externalTitle,
          );
        } catch (reason) {
          console.error(
            `Error loading non-booking tutor calendar events for ${tutor.tutorId}:`,
            reason,
          );
          return [] as TutorCalendarLessonEvent[];
        }
      }),
    ),
  ]);

  const bookings =
    bookingsSettled.status === 'fulfilled' ? bookingsSettled.value : [];
  if (bookingsSettled.status === 'rejected') {
    console.error('Error loading host tutor bookings for calendar:', bookingsSettled.reason);
  }

  const bookingEvents = bookings
    .filter((booking) => tutorIds.has(booking.tutorId))
    .map((booking) => {
      const tz = tutorTimezoneById.get(booking.tutorId) || spaceTimezone;
      const mapped = mapBookingToLessonEvent(booking, bookingTitleFallback, tz, walkInLabel);
      return {
        ...mapped,
        tutorName: mapped.tutorName?.trim() || tutorNameById.get(booking.tutorId) || null,
      };
    });

  const calendarEvents: TutorCalendarLessonEvent[] = [];
  nonBookingByTutor.forEach((rows, index) => {
    const tutor = tutors[index];
    const tutorTimezone = tutorTimezoneById.get(tutor.tutorId) || spaceTimezone;
    for (const calendarEvent of rows) {
      // HOST_BOOKING/manual rows that back a tutor booking must not hide the
      // booking's room on the unified grid.
      const overlapsBooking = bookingEvents.some(
        (booking) =>
          booking.tutorId === tutor.tutorId &&
          calendarEventOverlapsBooking(calendarEvent, booking, tutorTimezone),
      );
      if (overlapsBooking) continue;
      calendarEvents.push({
        ...calendarEvent,
        tutorId: tutor.tutorId,
        tutorName: tutorNameById.get(tutor.tutorId) || null,
      });
    }
  });

  return [...bookingEvents, ...calendarEvents].sort(
    (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
  );
}

async function loadNonBookingCalendarEventsForRange(
  tutorId: string,
  rangeStart: Date,
  rangeEnd: Date,
  externalTitle: string,
): Promise<TutorCalendarLessonEvent[]> {
  const out: TutorCalendarLessonEvent[] = [];
  const cursor = new Date(rangeStart);
  cursor.setHours(0, 0, 0, 0);
  const endDay = new Date(rangeEnd);
  endDay.setHours(0, 0, 0, 0);

  while (cursor.getTime() <= endDay.getTime()) {
    const day = formatLocalDateKey(cursor);
    try {
      const rows = await tutorApiClient.getTutorDailyAvailability([tutorId], day);
      const row = rows[0];
      if (row?.calendarEvents?.length) {
        for (const ev of row.calendarEvents) {
          if (ev.source === CalendarEventSource.HOST_BOOKING) {
            out.push({
              id: `manual-${ev.id}`,
              calendarEventId: ev.id,
              title: ev.title?.trim() || 'Block',
              startTime: stripCalendarInstant(ev.startTime),
              endTime: stripCalendarInstant(ev.endTime),
              kind: 'manual',
              status: ev.status,
              tutorId,
            });
            continue;
          }
          if (ev.source !== CalendarEventSource.GOOGLE_CALENDAR_SYNCED_BOOKING) {
            continue;
          }
          out.push({
            id: `calendar-${ev.id}`,
            calendarEventId: ev.id,
            title: ev.title?.trim() || externalTitle,
            startTime: stripCalendarInstant(ev.startTime),
            endTime: stripCalendarInstant(ev.endTime),
            kind: 'external',
            status: ev.status,
            tutorId,
          });
        }
      }
    } catch (error) {
      console.error(`Error loading tutor daily availability for ${tutorId} on ${day}:`, error);
    }
    cursor.setDate(cursor.getDate() + 1);
  }

  return out;
}

function mapPublicAvailabilityEventToCalendarEvent(
  ev: { id: string; title?: string | null; startTime: string; endTime: string; status?: string | null },
): CalendarEvent | null {
  const status = ev.status?.toUpperCase() ?? CalendarEventStatus.CONFIRMED;
  if (status === CalendarEventStatus.CANCELLED) {
    return null;
  }
  // Pending tutor bookings must not block slot selection for other students.
  if (status === CalendarEventStatus.AWAITING_CONFIRMATION) {
    return null;
  }
  return {
    id: ev.id,
    source: CalendarEventSource.BOOKING,
    title: ev.title?.trim() || '',
    startTime: stripCalendarInstant(ev.startTime),
    endTime: stripCalendarInstant(ev.endTime),
    status: CalendarEventStatus.CONFIRMED,
  };
}

/** Busy blocks for public tutor booking UI (daily availability API). */
export async function loadPublicTutorBusyCalendarEventsForRange(
  tutorId: string,
  rangeStart: Date,
  rangeEnd: Date,
): Promise<CalendarEvent[]> {
  const out: CalendarEvent[] = [];
  const cursor = new Date(rangeStart);
  cursor.setHours(0, 0, 0, 0);
  const endDay = new Date(rangeEnd);
  endDay.setHours(0, 0, 0, 0);

  while (cursor.getTime() <= endDay.getTime()) {
    const day = formatLocalDateKey(cursor);
    const rows = await tutorApiClient.getTutorDailyAvailability([tutorId], day);
    const row = rows[0];
    if (row?.calendarEvents?.length) {
      for (const ev of row.calendarEvents) {
        const mapped = mapPublicAvailabilityEventToCalendarEvent(ev);
        if (mapped) {
          out.push(mapped);
        }
      }
    }
    cursor.setDate(cursor.getDate() + 1);
  }

  return out;
}

export const TUTOR_CALENDAR_COLORS = {
  confirmed: '#C9A227',
  pending: '#E07A5F',
  external: '#6B7B8C',
  manual: '#FFB648',
  workingHours: 'rgba(34, 163, 120, 0.09)',
} as const;

/** Tutor identity colors — 10 distinct hues, spaced away from room warm/primary set. */
export const TUTOR_IDENTITY_COLORS = [
  '#EC4899', // Hot pink
  '#6366F1', // Indigo
  '#14B8A6', // Teal
  '#A855F7', // Purple
  '#06B6D4', // Cyan
  '#22C55E', // Emerald
  '#0EA5E9', // Sky
  '#F43F5E', // Rose
  '#84CC16', // Lime
  '#64748B', // Slate
] as const;

const COLOR_DISTANCE_THRESHOLD = 90;

function parseHexColor(input: string): [number, number, number] | null {
  const hex = input.trim().replace(/^#/, '');
  if (/^[0-9a-fA-F]{6}$/.test(hex)) {
    return [parseInt(hex.slice(0, 2), 16), parseInt(hex.slice(2, 4), 16), parseInt(hex.slice(4, 6), 16)];
  }
  if (/^[0-9a-fA-F]{3}$/.test(hex)) {
    return [
      parseInt(hex[0] + hex[0], 16),
      parseInt(hex[1] + hex[1], 16),
      parseInt(hex[2] + hex[2], 16),
    ];
  }
  return null;
}

function colorDistance(a: string, b: string): number {
  const left = parseHexColor(a);
  const right = parseHexColor(b);
  if (!left || !right) return Number.POSITIVE_INFINITY;
  const dr = left[0] - right[0];
  const dg = left[1] - right[1];
  const db = left[2] - right[2];
  return Math.sqrt(dr * dr + dg * dg + db * db);
}

function isColorTooClose(candidate: string, occupied: string[]): boolean {
  return occupied.some((color) => colorDistance(candidate, color) < COLOR_DISTANCE_THRESHOLD);
}

function pickTutorPaletteColor(startIndex: number, occupied: string[]): string {
  const palette = TUTOR_IDENTITY_COLORS;
  for (let offset = 0; offset < palette.length; offset += 1) {
    const candidate = palette[(startIndex + offset) % palette.length];
    if (!isColorTooClose(candidate, occupied)) {
      return candidate;
    }
  }
  return palette[startIndex % palette.length];
}

/**
 * Stable per-tutor colors from roster order, skipping hues already used by rooms (or other tutors).
 * Prefer a persisted `color` when present.
 */
export function generateTutorColor(
  tutorId: string | undefined | null,
  tutors: { tutorId: string; color?: string | null }[],
  occupiedColors: string[] = [],
): string {
  const assigned = new Map<string, string>();
  const used = occupiedColors.filter((color) => Boolean(color?.trim()));

  tutors.forEach((tutor, index) => {
    const persisted = tutor.color?.trim();
    const color = persisted || pickTutorPaletteColor(index, used);
    assigned.set(tutor.tutorId, color);
    used.push(color);
  });

  if (tutorId && assigned.has(tutorId)) {
    return assigned.get(tutorId)!;
  }

  if (!tutorId) {
    return pickTutorPaletteColor(0, used);
  }

  let hash = 0;
  for (let i = 0; i < tutorId.length; i += 1) {
    hash = (hash * 31 + tutorId.charCodeAt(i)) >>> 0;
  }
  return pickTutorPaletteColor(hash, used);
}

/**
 * Event fill color for a tutor lesson on the host unified calendar.
 * Identity comes from the tutor; pending/external stay visually distinct via classNames.
 */
export function resolveHostTutorEventColor(
  event: Pick<TutorCalendarLessonEvent, 'tutorId' | 'kind'>,
  tutors: { tutorId: string; color?: string | null }[],
  occupiedColors: string[] = [],
): string {
  if (event.kind === 'external') {
    return TUTOR_CALENDAR_COLORS.external;
  }
  return generateTutorColor(event.tutorId, tutors, occupiedColors);
}
